import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { withLatestFrom } from 'rxjs/operators';

import {
  CompileDossierStart,
  DossierDelete,
  GetDossierStatus,
  GetPreviewDossierStatus,
  HandoverDossierActionTypes,
  LoadDossierFile,
  LoadDossierFileById,
  LoadDossierFileSuccess,
  LoadPreviewDossierFile,
  LoadPreviewDossierFileSuccess,
  PreviewDossierStart,
  RequestJobStatus,
  RequestPreviewJobStatus,
  ResetHandoverStatus,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentHandoverId, getCurrentProject } from '@completion/selectors';
import { HandoverDossierService } from '@completion/services';
import { ResourceEffect } from './resource.effect';
import { trackResource } from './resource-fetch.util';
import { DossierStatus, DossierUrl } from '@completion/models';

@Injectable()
export class HandoverDossierEffect extends ResourceEffect {
  constructor(
    readonly actions$: Actions,
    readonly handoverDossierService: HandoverDossierService,
    readonly store: Store<State>,
    readonly toastr: NotificationService,
  ) {
    super(actions$, handoverDossierService, store, toastr);
  }

  startCompileDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CompileDossierStart>(HandoverDossierActionTypes.CompileDossierStart),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierStatus>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'startCompileDossier',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new GetDossierStatus(response),
      }),
    ),
  );

  startPreviewDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PreviewDossierStart>(HandoverDossierActionTypes.PreviewDossierStart),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierStatus>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'startPreviewDossier',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new GetPreviewDossierStatus(response),
      }),
    ),
  );

  getDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDossierFile>(HandoverDossierActionTypes.LoadDossierFile),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierUrl>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'getDossier',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new LoadDossierFileSuccess(response),
      }),
    ),
  );

  getDossierById$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDossierFileById>(HandoverDossierActionTypes.LoadDossierFileById),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId)),
      trackResource<DossierUrl>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'getDossier',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId],
        onSuccess: (response) => new LoadDossierFileSuccess(response),
      }),
    ),
  );

  getPreviewDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPreviewDossierFile>(HandoverDossierActionTypes.LoadPreviewDossierFile),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierUrl>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'getPreviewDossier',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new LoadPreviewDossierFileSuccess(response),
      }),
    ),
  );

  deleteDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DossierDelete>(HandoverDossierActionTypes.DossierDelete),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'deleteDossier',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: () => ({ type: '[Handover Dossier API] Delete Dossier Success' }),
      }),
    ),
  );

  getJobStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestJobStatus>(HandoverDossierActionTypes.RequestJobStatus),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierStatus>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'getJobStatus',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new GetDossierStatus(response),
      }),
    ),
  );

  getPreviewJobStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestPreviewJobStatus>(HandoverDossierActionTypes.RequestPreviewJobStatus),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId, getCurrentHandoverId)),
      trackResource<DossierStatus>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'getPreviewJobStatus',
        serviceArgs: ([_, currentProject, cpId, handoverId]) => [currentProject.id, cpId, handoverId],
        onSuccess: (response) => new GetPreviewDossierStatus(response),
      }),
    ),
  );

  resetHandoverStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetHandoverStatus>(HandoverDossierActionTypes.ResetHandoverStatus),
      withLatestFrom(...this.multiSelect(getCurrentProject, getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverDossierService,
        method: 'resetHandoverStatus',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, (action as ResetHandoverStatus).handoverId],
        onSuccess: () => new RequestJobStatus(),
      }),
    ),
  );
}
